import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import {
    Sheet,
    SheetContent,
    SheetHeader,
    SheetTitle,
    SheetDescription,
} from "../ui/sheet";
import { useCart } from "../../context/CartContext";
import { useCustomerAuth } from "../../context/CustomerAuthContext";
import {
    Minus,
    Plus,
    Trash2,
    ShoppingBag,
    Loader2,
    CreditCard,
    LogIn,
    UserPlus,
    MapPin,
    Lock,
    Truck,
    Snowflake,
    Leaf,
    Ticket,
    X,
} from "lucide-react";
import { API } from "../../lib/api";
import { toast } from "sonner";

const EMPTY_ADDR = {
    full_name: "",
    phone: "",
    line1: "",
    line2: "",
    postal_code: "",
    city: "",
    country: "Portugal",
};

export const CartDrawer = () => {
    const {
        items,
        updateQty,
        removeItem,
        totals,
        open,
        setOpen,
        clear,
        coupon,
        setCoupon,
        clearCoupon,
    } = useCart();
    const { token, user, refreshMe } = useCustomerAuth();

    const [loading, setLoading] = useState(false);
    const [addr, setAddr] = useState(EMPTY_ADDR);
    const [selectedAddrId, setSelectedAddrId] = useState("");
    const [shipCfg, setShipCfg] = useState({
        free_shipping_threshold: 25,
        shipping_fee: 4.9,
    });
    const [couponInput, setCouponInput] = useState("");
    const [applyingCoupon, setApplyingCoupon] = useState(false);

    useEffect(() => {
        axios
            .get(`${API}/checkout/config`)
            .then((r) => setShipCfg(r.data))
            .catch(() => {});
    }, []);

    const baseShippingFee =
        totals.subtotal >= shipCfg.free_shipping_threshold ? 0 : shipCfg.shipping_fee;
    const freeShipping = baseShippingFee === 0;
    const missingForFree = Math.max(
        0,
        shipCfg.free_shipping_threshold - totals.subtotal,
    );
    const shipProgress = Math.min(
        100,
        (totals.subtotal / shipCfg.free_shipping_threshold) * 100,
    );

    // Effective totals — apply coupon if present
    const discount = coupon ? Number(coupon.discount || 0) : 0;
    const effectiveShippingFee =
        coupon?.coupon?.type === "free_shipping" ? 0 : baseShippingFee;
    const grandTotal = Math.max(
        0,
        +(totals.subtotal + effectiveShippingFee - discount).toFixed(2),
    );

    // Re-validate coupon whenever subtotal changes (so discount stays coherent)
    useEffect(() => {
        if (!coupon?.coupon?.code) return;
        if (items.length === 0) {
            clearCoupon();
            return;
        }
        axios
            .post(`${API}/checkout/validate-coupon`, {
                code: coupon.coupon.code,
                subtotal: totals.subtotal,
                shipping_fee: baseShippingFee,
            })
            .then((r) =>
                setCoupon({
                    coupon: r.data.coupon,
                    discount: r.data.discount,
                    shipping_fee: r.data.shipping_fee,
                }),
            )
            .catch(() => clearCoupon());
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [totals.subtotal, baseShippingFee]);

    const applyCoupon = async () => {
        const code = couponInput.trim();
        if (!code) return;
        if (items.length === 0) {
            toast.error("Adiciona artigos ao carrinho primeiro");
            return;
        }
        setApplyingCoupon(true);
        try {
            const { data } = await axios.post(`${API}/checkout/validate-coupon`, {
                code,
                subtotal: totals.subtotal,
                shipping_fee: baseShippingFee,
            });
            setCoupon({
                coupon: data.coupon,
                discount: data.discount,
                shipping_fee: data.shipping_fee,
            });
            setCouponInput("");
            toast.success(`Cupão ${data.coupon.code} aplicado`);
        } catch (err) {
            toast.error(err.response?.data?.detail || "Cupão inválido");
        } finally {
            setApplyingCoupon(false);
        }
    };

    // Pre-fill / refresh on auth changes
    useEffect(() => {
        if (open && token && !user) {
            refreshMe();
        }
    }, [open, token, user, refreshMe]);

    const userAddresses = useMemo(() => user?.addresses || [], [user]);

    useEffect(() => {
        if (!user) {
            setAddr(EMPTY_ADDR);
            setSelectedAddrId("");
            return;
        }
        // Pick default address (or first), pre-fill the form
        const def =
            userAddresses.find((a) => a.is_default) || userAddresses[0] || null;
        if (def) {
            setSelectedAddrId(def.id);
            setAddr({
                full_name: def.full_name || user.name || "",
                phone: def.phone || user.phone || "",
                line1: def.line1 || "",
                line2: def.line2 || "",
                postal_code: def.postal_code || "",
                city: def.city || "",
                country: def.country || "Portugal",
            });
        } else {
            setSelectedAddrId("");
            setAddr((a) => ({
                ...a,
                full_name: user.name || a.full_name,
                phone: user.phone || a.phone,
            }));
        }
    }, [user, userAddresses]);

    const handlePickAddr = (id) => {
        setSelectedAddrId(id);
        const a = userAddresses.find((x) => x.id === id);
        if (a) {
            setAddr({
                full_name: a.full_name || user?.name || "",
                phone: a.phone || user?.phone || "",
                line1: a.line1 || "",
                line2: a.line2 || "",
                postal_code: a.postal_code || "",
                city: a.city || "",
                country: a.country || "Portugal",
            });
        }
    };

    const setField = (k) => (e) => setAddr((p) => ({ ...p, [k]: e.target.value }));

    const checkout = async () => {
        if (items.length === 0) return;
        if (!token || !user) {
            toast.error("Inicia sessão para finalizar a compra");
            return;
        }
        if (!addr.line1 || !addr.postal_code || !addr.city) {
            toast.error("Preenche a morada de envio (rua, código postal e cidade)");
            return;
        }
        setLoading(true);
        try {
            const payload = {
                items: items.map((i) => ({ product_id: i.id, quantity: i.quantity })),
                origin_url: window.location.origin,
                customer_name: addr.full_name || user.name || undefined,
                customer_email: user.email,
                shipping_address: {
                    full_name: addr.full_name || user.name || undefined,
                    phone: addr.phone || undefined,
                    line1: addr.line1.trim(),
                    line2: addr.line2 ? addr.line2.trim() : undefined,
                    postal_code: addr.postal_code.trim(),
                    city: addr.city.trim(),
                    country: addr.country || "Portugal",
                },
                coupon_code: coupon?.coupon?.code || undefined,
            };
            const { data } = await axios.post(`${API}/checkout/session`, payload, {
                headers: { Authorization: `Bearer ${token}` },
            });
            window.location.href = data.url;
        } catch (err) {
            console.error(err);
            toast.error(err.response?.data?.detail || "Erro a iniciar pagamento");
            setLoading(false);
        }
    };

    return (
        <Sheet open={open} onOpenChange={setOpen}>
            <SheetContent
                side="right"
                className="w-full sm:max-w-md p-0 flex flex-col bg-white"
                data-testid="cart-drawer"
            >
                <SheetHeader className="px-4 sm:px-6 py-4 sm:py-5 border-b border-brand-green/10 text-left">
                    <SheetTitle className="font-display font-medium text-xl sm:text-2xl text-brand-ink flex items-center gap-2 pr-8">
                        <ShoppingBag className="h-5 w-5 text-brand-green shrink-0" /> Carrinho
                    </SheetTitle>
                    <SheetDescription className="sr-only">
                        Revê os artigos do teu carrinho, ajusta quantidades e finaliza a compra.
                    </SheetDescription>
                </SheetHeader>

                <div className="flex-1 overflow-y-auto px-4 sm:px-6 py-4 space-y-3">
                    {items.length > 0 && (
                        <div
                            className={`rounded-2xl p-3.5 ${
                                freeShipping ? "bg-brand-green-soft" : "bg-brand-cream"
                            }`}
                            data-testid="free-shipping-bar"
                        >
                            <div className="flex items-center gap-2 text-xs font-semibold text-brand-ink">
                                <Truck
                                    className={`h-4 w-4 shrink-0 ${
                                        freeShipping
                                            ? "text-brand-green"
                                            : "text-brand-orange"
                                    }`}
                                />
                                {freeShipping ? (
                                    <span data-testid="free-shipping-unlocked">
                                        Envio grátis desbloqueado! 🎉
                                    </span>
                                ) : (
                                    <span data-testid="free-shipping-missing">
                                        Faltam{" "}
                                        <strong className="text-brand-orange">
                                            {missingForFree.toFixed(2).replace(".", ",")} €
                                        </strong>{" "}
                                        para envio grátis
                                    </span>
                                )}
                            </div>
                            <div className="mt-2 h-1.5 rounded-full bg-white overflow-hidden">
                                <div
                                    className={`h-full rounded-full transition-all duration-500 ${
                                        freeShipping
                                            ? "bg-brand-green"
                                            : "bg-brand-orange"
                                    }`}
                                    style={{ width: `${shipProgress}%` }}
                                />
                            </div>
                        </div>
                    )}
                    {items.length === 0 ? (
                        <div className="text-center py-16">
                            <div className="mx-auto h-20 w-20 rounded-full bg-brand-green-soft flex items-center justify-center">
                                <ShoppingBag className="h-9 w-9 text-brand-green" />
                            </div>
                            <p className="mt-5 text-brand-ink-soft">O teu carrinho está vazio.</p>
                            <button
                                type="button"
                                onClick={() => setOpen(false)}
                                className="mt-6 inline-flex items-center justify-center rounded-full bg-brand-green text-white px-5 py-3 text-sm font-semibold"
                                data-testid="empty-cart-cta"
                            >
                                Ver sabores
                            </button>
                        </div>
                    ) : (
                        items.map((i) => (
                            <article
                                key={i.id}
                                data-testid={`cart-item-${i.id}`}
                                className="flex gap-3 p-3 rounded-2xl bg-brand-cream"
                            >
                                <img
                                    src={i.image}
                                    alt={i.name}
                                    className="h-16 w-16 sm:h-20 sm:w-20 rounded-xl object-cover shrink-0"
                                />
                                <div className="flex-1 min-w-0">
                                    <div className="flex items-start justify-between gap-2">
                                        <h4 className="font-display font-medium text-brand-ink text-sm sm:text-base truncate">
                                            {i.name}
                                        </h4>
                                        <button
                                            type="button"
                                            onClick={() => removeItem(i.id)}
                                            data-testid={`cart-remove-${i.id}`}
                                            className="text-brand-ink/40 hover:text-red-500 transition-colors shrink-0"
                                            aria-label="Remover"
                                        >
                                            <Trash2 className="h-4 w-4" />
                                        </button>
                                    </div>
                                    <div className="text-xs sm:text-sm text-brand-ink-soft mt-0.5">
                                        {i.price.toFixed(2).replace(".", ",")} €
                                    </div>
                                    <div className="mt-2 flex items-center justify-between gap-2">
                                        <div className="inline-flex items-center bg-white rounded-full p-1 shadow-soft">
                                            <button
                                                type="button"
                                                onClick={() => updateQty(i.id, i.quantity - 1)}
                                                data-testid={`cart-decrease-${i.id}`}
                                                className="h-7 w-7 rounded-full hover:bg-brand-green-soft flex items-center justify-center"
                                                aria-label="-"
                                            >
                                                <Minus className="h-3.5 w-3.5" />
                                            </button>
                                            <span className="px-3 text-sm font-semibold min-w-[2ch] text-center">
                                                {i.quantity}
                                            </span>
                                            <button
                                                type="button"
                                                onClick={() => updateQty(i.id, i.quantity + 1)}
                                                data-testid={`cart-increase-${i.id}`}
                                                className="h-7 w-7 rounded-full hover:bg-brand-green-soft flex items-center justify-center"
                                                aria-label="+"
                                            >
                                                <Plus className="h-3.5 w-3.5" />
                                            </button>
                                        </div>
                                        <div className="font-display font-medium text-brand-ink text-sm sm:text-base">
                                            {(i.price * i.quantity).toFixed(2).replace(".", ",")} €
                                        </div>
                                    </div>
                                </div>
                            </article>
                        ))
                    )}
                </div>

                {items.length > 0 && (
                    <div className="border-t border-brand-green/10 bg-white flex flex-col">
                        {/* Scrollable region: address form OR auth callout */}
                        <div className="px-4 sm:px-6 pt-4 pb-2 overflow-y-auto max-h-[40vh]">
                            {!token || !user ? (
                                <div
                                    data-testid="cart-auth-required"
                                    className="w-full rounded-2xl bg-brand-orange-soft border-2 border-brand-orange/40 p-3 sm:p-4 space-y-3"
                                >
                                    <div className="flex items-start gap-2">
                                        <Lock className="h-4 w-4 text-brand-orange shrink-0 mt-0.5" />
                                        <div className="text-sm text-brand-ink min-w-0">
                                            <strong className="font-display block">
                                                Inicia sessão para continuar
                                            </strong>
                                            <p className="mt-1 text-xs text-brand-ink-soft leading-snug">
                                                Precisas de uma conta para finalizar a compra. Assim ficas com o histórico de encomendas e moradas guardadas.
                                            </p>
                                        </div>
                                    </div>
                                    <div className="grid grid-cols-2 gap-2">
                                        <Link
                                            to="/conta/entrar"
                                            onClick={() => setOpen(false)}
                                            data-testid="cart-go-login"
                                            className="inline-flex items-center justify-center gap-1.5 rounded-full bg-brand-green hover:bg-brand-green-dark text-white px-3 py-2 text-xs font-semibold transition-colors whitespace-nowrap"
                                        >
                                            <LogIn className="h-3.5 w-3.5" /> Entrar
                                        </Link>
                                        <Link
                                            to="/conta/registar"
                                            onClick={() => setOpen(false)}
                                            data-testid="cart-go-register"
                                            className="inline-flex items-center justify-center gap-1.5 rounded-full bg-white border-2 border-brand-green/30 hover:border-brand-green text-brand-ink px-3 py-2 text-xs font-semibold transition-colors whitespace-nowrap"
                                        >
                                            <UserPlus className="h-3.5 w-3.5" /> Registar
                                        </Link>
                                    </div>
                                </div>
                            ) : (
                                <div className="w-full space-y-2.5" data-testid="cart-shipping-form">
                                    <div className="text-[10px] uppercase tracking-widest font-bold text-brand-ink-soft flex items-center gap-1.5">
                                        <MapPin className="h-3 w-3" /> Morada de envio
                                    </div>
                                    {userAddresses.length > 0 && (
                                        <select
                                            value={selectedAddrId}
                                            onChange={(e) => handlePickAddr(e.target.value)}
                                            data-testid="cart-address-select"
                                            className="w-full rounded-xl border-2 border-brand-green/15 bg-white px-3 py-2 text-xs font-semibold text-brand-ink focus:outline-none focus:border-brand-green"
                                        >
                                            {userAddresses.map((a) => (
                                                <option key={a.id} value={a.id}>
                                                    {a.line1}
                                                    {a.postal_code ? `, ${a.postal_code}` : ""}{" "}
                                                    {a.city}
                                                    {a.is_default ? " (predefinida)" : ""}
                                                </option>
                                            ))}
                                            <option value="">+ Nova morada</option>
                                        </select>
                                    )}
                                    <div className="grid grid-cols-2 gap-2">
                                        <input
                                            value={addr.full_name}
                                            onChange={setField("full_name")}
                                            placeholder="Nome completo"
                                            data-testid="cart-input-fullname"
                                            className={inp}
                                        />
                                        <input
                                            value={addr.phone}
                                            onChange={setField("phone")}
                                            placeholder="Telefone"
                                            data-testid="cart-input-phone"
                                            className={inp}
                                        />
                                    </div>
                                    <input
                                        value={addr.line1}
                                        onChange={setField("line1")}
                                        placeholder="Morada (rua, nº)"
                                        data-testid="cart-input-line1"
                                        className={inp}
                                    />
                                    <input
                                        value={addr.line2}
                                        onChange={setField("line2")}
                                        placeholder="Andar / complemento (opcional)"
                                        data-testid="cart-input-line2"
                                        className={inp}
                                    />
                                    <div className="grid grid-cols-2 gap-2">
                                        <input
                                            value={addr.postal_code}
                                            onChange={setField("postal_code")}
                                            placeholder="Código postal"
                                            data-testid="cart-input-postal"
                                            className={inp}
                                        />
                                        <input
                                            value={addr.city}
                                            onChange={setField("city")}
                                            placeholder="Cidade"
                                            data-testid="cart-input-city"
                                            className={inp}
                                        />
                                    </div>
                                    <input
                                        value={addr.country}
                                        onChange={setField("country")}
                                        placeholder="País"
                                        data-testid="cart-input-country"
                                        className={inp}
                                    />
                                </div>
                            )}
                        </div>

                        {/* Sticky checkout block */}
                        <div className="px-4 sm:px-6 pt-3 pb-4 sm:pb-5 border-t border-brand-green/10 bg-brand-cream/40 space-y-3">
                            {/* Coupon */}
                            <div data-testid="cart-coupon-block">
                                {coupon ? (
                                    <div
                                        className="flex items-center gap-2 rounded-full bg-brand-green-soft px-3 py-2"
                                        data-testid="cart-coupon-applied"
                                    >
                                        <Ticket className="h-3.5 w-3.5 text-brand-green shrink-0" />
                                        <div className="flex-1 min-w-0 text-xs">
                                            <span className="font-bold text-brand-green">
                                                {coupon.coupon.code}
                                            </span>
                                            <span className="text-brand-ink-soft ml-1.5">
                                                {coupon.coupon.type === "percent" &&
                                                    `${coupon.coupon.value}% off`}
                                                {coupon.coupon.type === "fixed" &&
                                                    `${coupon.coupon.value.toFixed(2).replace(".", ",")} € off`}
                                                {coupon.coupon.type === "free_shipping" &&
                                                    "Envio grátis"}
                                            </span>
                                        </div>
                                        <button
                                            type="button"
                                            onClick={clearCoupon}
                                            data-testid="cart-coupon-remove"
                                            className="p-1 rounded-full hover:bg-white/60 text-brand-ink-soft"
                                            aria-label="Remover cupão"
                                        >
                                            <X className="h-3.5 w-3.5" />
                                        </button>
                                    </div>
                                ) : (
                                    <div className="flex items-center gap-2">
                                        <div className="relative flex-1 min-w-0">
                                            <Ticket className="absolute left-3 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-brand-ink-soft" />
                                            <input
                                                value={couponInput}
                                                onChange={(e) =>
                                                    setCouponInput(e.target.value.toUpperCase())
                                                }
                                                onKeyDown={(e) => {
                                                    if (e.key === "Enter") {
                                                        e.preventDefault();
                                                        applyCoupon();
                                                    }
                                                }}
                                                placeholder="Código de desconto"
                                                data-testid="cart-coupon-input"
                                                className="w-full rounded-full border-2 border-brand-green/15 focus:border-brand-green bg-white pl-9 pr-3 py-2 text-xs font-mono font-bold uppercase text-brand-ink focus:outline-none"
                                            />
                                        </div>
                                        <button
                                            type="button"
                                            onClick={applyCoupon}
                                            disabled={applyingCoupon || !couponInput.trim()}
                                            data-testid="cart-coupon-apply"
                                            className="rounded-full bg-brand-ink hover:bg-[#2C312E] text-white px-4 py-2 text-xs font-bold uppercase tracking-widest disabled:opacity-50 whitespace-nowrap"
                                        >
                                            {applyingCoupon ? "..." : "Aplicar"}
                                        </button>
                                    </div>
                                )}
                            </div>

                            <div className="space-y-1" data-testid="cart-totals">
                                <div className="flex items-center justify-between text-sm text-brand-ink-soft">
                                    <span>Subtotal</span>
                                    <span data-testid="cart-subtotal">
                                        {totals.subtotal.toFixed(2).replace(".", ",")} €
                                    </span>
                                </div>
                                <div className="flex items-center justify-between text-sm text-brand-ink-soft">
                                    <span>Envio</span>
                                    <span data-testid="cart-shipping">
                                        {effectiveShippingFee === 0 ? (
                                            <span className="font-bold text-brand-green">
                                                Grátis
                                            </span>
                                        ) : (
                                            `${effectiveShippingFee.toFixed(2).replace(".", ",")} €`
                                        )}
                                    </span>
                                </div>
                                {discount > 0 && (
                                    <div
                                        className="flex items-center justify-between text-sm text-brand-green font-semibold"
                                        data-testid="cart-discount"
                                    >
                                        <span>Desconto ({coupon?.coupon?.code})</span>
                                        <span>
                                            -{discount.toFixed(2).replace(".", ",")} €
                                        </span>
                                    </div>
                                )}
                                <div className="flex items-center justify-between pt-1">
                                    <span className="text-sm font-semibold text-brand-ink">
                                        Total
                                    </span>
                                    <span
                                        className="font-display font-medium text-2xl sm:text-3xl text-brand-ink"
                                        data-testid="cart-total"
                                    >
                                        {grandTotal.toFixed(2).replace(".", ",")} €
                                    </span>
                                </div>
                            </div>

                            <button
                                type="button"
                                onClick={checkout}
                                disabled={loading || !token || !user}
                                data-testid="checkout-button"
                                className="w-full inline-flex items-center justify-center gap-2 rounded-full bg-brand-green hover:bg-brand-green-dark text-white px-4 py-3 text-sm sm:text-base font-semibold shadow-pop transition-all disabled:opacity-60 disabled:cursor-not-allowed whitespace-nowrap"
                            >
                                {loading ? (
                                    <Loader2 className="h-4 w-4 animate-spin shrink-0" />
                                ) : !token || !user ? (
                                    <Lock className="h-4 w-4 shrink-0" />
                                ) : (
                                    <CreditCard className="h-4 w-4 shrink-0" />
                                )}
                                <span className="truncate">
                                    {loading
                                        ? "A processar..."
                                        : !token || !user
                                            ? "Inicia sessão para comprar"
                                            : "Finalizar compra"}
                                </span>
                            </button>
                            <div
                                className="flex items-center justify-center gap-4 text-[10px] text-brand-ink-soft"
                                data-testid="cart-trust-badges"
                            >
                                <span className="inline-flex items-center gap-1">
                                    <Snowflake className="h-3 w-3 text-brand-green" />
                                    Envio em frio 48h
                                </span>
                                <span className="inline-flex items-center gap-1">
                                    <Lock className="h-3 w-3 text-brand-green" />
                                    Pagamento seguro Stripe
                                </span>
                                <span className="inline-flex items-center gap-1">
                                    <Leaf className="h-3 w-3 text-brand-green" />
                                    100% fruta
                                </span>
                            </div>
                            <button
                                type="button"
                                onClick={clear}
                                className="block mx-auto text-xs text-brand-ink-soft hover:text-red-500 underline-offset-2 hover:underline whitespace-nowrap"
                                data-testid="cart-clear"
                            >
                                Limpar carrinho
                            </button>
                        </div>
                    </div>
                )}
            </SheetContent>
        </Sheet>
    );
};

const inp =
    "w-full rounded-xl border-2 border-brand-green/15 bg-white px-3 py-2 text-xs text-brand-ink focus:outline-none focus:border-brand-green";
